<template>
  <div class="calm">
    <!-- ═══════════════ HEADER ═══════════════ -->
    <header class="calm__header">
      <div class="calm__header__row">
        <div>
          <div class="c-eyebrow">Branch Money</div>
          <h1 class="calm__header__title">{{ $t("menu.branch_money") }}</h1>
          <div class="calm__header__sub">
            <span class="finance-active-round">
              <i class="fas fa-wallet"></i>
              <strong>{{ currentPeriodLabel }}</strong>
            </span>
            <span class="calm__header__sep">·</span>
            <span><strong class="c-mono">{{ formatNum(policyStatus.length) }}</strong> รายการ</span>
            <span class="calm__header__sep">·</span>
            <span class="c-mono c-mute">วันนี้ {{ todayStamp }}</span>
          </div>
        </div>
      </div>
    </header>

    <div class="calm__body">
      <!-- ═══════════════ STAT CARDS — pool float by type ═══════════════ -->
      <div class="calm__stat-grid">
        <div class="calm__stat calm__stat--blue">
          <div class="calm__stat__label">{{ $t("lmg.pool_transfer_total") }}</div>
          <div class="calm__stat__value">
            {{ formatBaht(typeBalance(2)) }}
            <span class="calm__stat__unit">บาท</span>
          </div>
          <div class="calm__stat__meta">เข้า {{ formatBaht(typeSumIn(2)) }} · ออก {{ formatBaht(typeSumOut(2)) }}</div>
        </div>
        <div class="calm__stat calm__stat--success">
          <div class="calm__stat__label">{{ $t("lmg.pool_cash_total") }}</div>
          <div class="calm__stat__value">
            {{ formatBaht(typeBalance(3)) }}
            <span class="calm__stat__unit">บาท</span>
          </div>
          <div class="calm__stat__meta">เข้า {{ formatBaht(typeSumIn(3)) }} · ออก {{ formatBaht(typeSumOut(3)) }}</div>
        </div>
        <div class="calm__stat calm__stat--warning">
          <div class="calm__stat__label">{{ $t("lmg.pool_reserve_total") }}</div>
          <div class="calm__stat__value">
            {{ formatBaht(typeBalance(0)) }}
            <span class="calm__stat__unit">บาท</span>
          </div>
          <div class="calm__stat__meta">เข้า {{ formatBaht(typeSumIn(0)) }} · ออก {{ formatBaht(typeSumOut(0)) }}</div>
        </div>
        <div class="calm__stat">
          <div class="calm__stat__label">{{ $t("lmg.pool_total") }}</div>
          <div class="calm__stat__value">
            {{ formatBaht(poolTotal.balance) }}
            <span class="calm__stat__unit">บาท</span>
          </div>
          <div class="calm__stat__meta">เข้า {{ formatBaht(poolTotal.sum_in) }} · ออก {{ formatBaht(poolTotal.sum_out) }}</div>
        </div>
      </div>

      <!-- ═══════════════ MOVEMENT (only when a date range is given) ═══════════════ -->
      <div class="calm__card" v-if="movement.has_range">
        <div class="calm__card__head">
          <div>
            <div class="calm__card__title">การเคลื่อนไหวในช่วงเวลา</div>
            <div class="calm__card__hint">{{ rangeLabel }}</div>
          </div>
        </div>
        <div class="cashrow">
          <div class="cashcell cashcell--cleared">
            <div class="cashcell__label">เงินเข้า</div>
            <div class="cashcell__value">{{ formatBaht(movement.sum_in) }} <span class="c-mono c-mute" style="font-size:12px">บาท</span></div>
          </div>
          <div class="cashcell cashcell--outstanding">
            <div class="cashcell__label">เงินออก</div>
            <div class="cashcell__value">{{ formatBaht(movement.sum_out) }} <span class="c-mono c-mute" style="font-size:12px">บาท</span></div>
          </div>
          <div class="cashcell">
            <div class="cashcell__label">สุทธิในช่วง</div>
            <div class="cashcell__value">{{ formatBaht(movementNet) }} <span class="c-mono c-mute" style="font-size:12px">บาท</span></div>
          </div>
        </div>
      </div>

      <!-- ═══════════════ PER-ACCOUNT BREAKDOWN (dynamic N) ═══════════════ -->
      <div class="calm__card">
        <div class="calm__card__head">
          <div>
            <div class="calm__card__title">{{ $t("lmg.pool_by_account") }}</div>
            <div class="calm__card__hint">ยอดคงเหลือต่อบัญชีรับเงิน</div>
          </div>
        </div>
        <div v-if="isLoadingBalance" class="acctgrid">
          <div class="acctcell" v-for="i in 3" :key="'ask-' + i">
            <div class="calm__skel" style="width:100%;height:18px;margin-bottom:8px"></div>
            <div class="calm__skel" style="width:60%;height:22px"></div>
          </div>
        </div>
        <div v-else-if="!byAccount.length" class="calm__empty" style="padding:28px 0">
          <div class="c-empty-icon"><i class="fas fa-folder-open"></i></div>
          <div class="c-empty-title">ไม่มีบัญชีที่มียอดเคลื่อนไหว</div>
        </div>
        <!-- v-for over ByAccount — N accounts is dynamic, not hard-coded. -->
        <div v-else class="acctgrid">
          <div class="acctcell" v-for="(acc, i) in byAccount" :key="'acc-' + (acc.bank_id != null ? acc.bank_id : i) + '-' + acc.account_type">
            <div class="acctcell__label">
              {{ acc.bank_name || '—' }}
              <span class="calm__pill" :class="typePillClass(acc.account_type)">{{ typeLabel(acc.account_type) }}</span>
            </div>
            <div class="acctcell__value">{{ formatBaht(acc.balance) }} <span class="c-mono c-mute" style="font-size:12px">บาท</span></div>
            <div class="acctcell__meta">เข้า {{ formatBaht(acc.sum_in) }} · ออก {{ formatBaht(acc.sum_out) }}</div>
          </div>
        </div>
      </div>

      <!-- ═══════════════ FILTER BAR (sticky) ═══════════════ -->
      <div class="calm__filterbar">
        <div class="calm__filter-grid">
          <div class="fld">
            <label>{{ $t("lmg.round") }}</label>
            <el-select v-model="searchParams.round" placeholder="ไม่เลือก">
              <el-option v-for="o in roundOptions" :key="o.ID" :label="o.Name" :value="o.ID"></el-option>
            </el-select>
          </div>
          <div class="fld">
            <label>{{ $t("lmg.month") }}</label>
            <el-select v-model="searchParams.month" placeholder="เดือน">
              <el-option v-for="o in monthOptions" :key="o.ID" :label="o.Name" :value="o.ID"></el-option>
            </el-select>
          </div>
          <div class="fld">
            <label>{{ $t("lmg.year") }}</label>
            <el-select v-model="searchParams.year" placeholder="ปี">
              <el-option v-for="o in yearOptions" :key="o" :label="o" :value="o"></el-option>
            </el-select>
          </div>
          <div class="fld">
            <label>ตั้งแต่วันที่</label>
            <flat-picker
              class="form-control datepicker"
              v-model="searchParams.dateFrom"
              :config="dateConfig"
              placeholder="ตั้งแต่วันที่"
            ></flat-picker>
          </div>
          <div class="fld">
            <label>ถึงวันที่</label>
            <flat-picker
              class="form-control datepicker"
              v-model="searchParams.dateTo"
              :config="dateConfig"
              placeholder="ถึงวันที่"
            ></flat-picker>
          </div>
          <div class="fld">
            <label>&nbsp;</label>
            <button class="c-btn c-btn--primary" @click="search" :disabled="isLoadingBalance || isLoadingPolicyStatus">
              <i v-if="isLoadingBalance || isLoadingPolicyStatus" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-search"></i>
              ค้นหา
            </button>
          </div>
        </div>
      </div>

      <!-- ═══════════════ PER-POLICY STATUS TABLE ═══════════════ -->
      <div class="calm__card calm__card--flush" style="margin-top:16px">
        <div class="finance-table-head">
          <div>
            <div class="calm__card__title">{{ $t("lmg.policy_status") }}</div>
            <div class="calm__card__hint">รายกรมธรรม์: จ่ายแล้ว / วิธีชำระ / บัญชี / นำส่ง DMS</div>
          </div>
          <div class="finance-table-toolbar">
            <input
              v-model="searchQuery"
              type="text"
              class="ed"
              placeholder="ค้นหา policy / ลูกค้า / ตัวแทน..."
              style="width:280px;max-width:100%"
            />
            <div class="calm__tfoot__per">
              <span>แสดงต่อหน้า</span>
              <el-select v-model="pagination.perPage" style="width:90px">
                <el-option v-for="n in pagination.perPageOptions" :key="n" :label="n" :value="n"></el-option>
              </el-select>
            </div>
          </div>
        </div>

        <div class="finance-table-scroll">
          <table class="calm__table">
            <thead>
              <tr>
                <th class="marker">#</th>
                <th>หมายเลขกรมธรรม์</th>
                <th>ลูกค้า</th>
                <th>ตัวแทน</th>
                <th>{{ $t("lmg.paid_status") }}</th>
                <th>{{ $t("lmg.payment_method") }}</th>
                <th>{{ $t("lmg.account") }}</th>
                <th>DMS</th>
                <th class="num">ยอดชำระ</th>
              </tr>
            </thead>
            <tbody v-if="isLoadingPolicyStatus">
              <tr v-for="i in pagination.perPage" :key="'sk-' + i">
                <td colspan="9"><div class="calm__skel" style="width:100%"></div></td>
              </tr>
            </tbody>
            <tbody v-else-if="!queriedData.length">
              <tr>
                <td colspan="9">
                  <div class="calm__empty">
                    <div class="c-empty-icon"><i class="fas fa-folder-open"></i></div>
                    <div class="c-empty-title">ไม่พบรายการ</div>
                    <div class="c-empty-sub">ลองปรับเงื่อนไขด้านบนหรือล้างตัวกรอง</div>
                  </div>
                </td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr v-for="(row, idx) in queriedData" :key="(row.policy_number || 'p') + '-' + idx">
                <td class="marker">{{ formatMarker(from + idx + 1) }}</td>
                <td class="c-mono">{{ row.policy_number || '—' }}</td>
                <td>
                  <div class="c-primary">{{ row.customer_fname }} {{ row.customer_lname }}</div>
                </td>
                <td>
                  <div class="c-primary">{{ row.agent_fname }} {{ row.agent_lname }}</div>
                </td>
                <td>
                  <span class="calm__pill" :class="row.paid ? 'calm__pill--success' : 'calm__pill--neutral'">
                    {{ row.paid ? $t("lmg.paid_yes") : $t("lmg.paid_no") }}
                  </span>
                </td>
                <td>
                  <span v-if="row.paid" class="c-primary">{{ methodLabel(row.paid_via) }}</span>
                  <span v-else style="color:var(--c-text-mute)">—</span>
                </td>
                <td>
                  <template v-if="row.paid && row.bank_name">
                    <div class="c-primary">{{ row.bank_name }}</div>
                    <div class="c-secondary">{{ typeLabel(row.account_type) }}</div>
                  </template>
                  <span v-else style="color:var(--c-text-mute)">—</span>
                </td>
                <td>
                  <span class="calm__pill" :class="row.dms_submitted ? 'calm__pill--success' : 'calm__pill--neutral'">
                    {{ row.dms_submitted ? $t("lmg.dms_submitted") : $t("lmg.dms_not_submitted") }}
                  </span>
                </td>
                <td class="num">{{ formatBaht(row.pay_amount) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="calm__tfoot finance-table-foot" v-if="tableData.length">
          <div class="calm__tfoot__count">
            แสดง <strong>{{ from + 1 }}</strong>–<strong>{{ to }}</strong> จาก <strong>{{ total }}</strong> รายการ
          </div>
          <base-pagination
            v-model="pagination.currentPage"
            :per-page="pagination.perPage"
            :total="total"
          ></base-pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Select, Option } from "element-ui";
import { BasePagination } from "@/components";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import clientPaginationMixin from "./PaginatedTables/clientPaginationMixin";
import { mapGetters, mapActions } from "vuex";

// localStorage key for filter persistence — coming back restores the last view.
const LS_KEY = "lmg.branchmoney.filters";

export default {
  mixins: [clientPaginationMixin],
  components: {
    BasePagination,
    flatPicker,
    [Select.name]: Select,
    [Option.name]: Option,
  },
  data() {
    // Hydrate filters from localStorage so the user's last view is restored.
    let saved = {};
    try {
      saved = JSON.parse(localStorage.getItem(LS_KEY) || "{}") || {};
    } catch (_) { saved = {}; }

    return {
      propsToSearch: ["policy_number", "customer_fname", "customer_lname", "agent_fname", "agent_lname"],
      searchParams: {
        round:    saved.round    != null ? saved.round    : 0,
        month:    saved.month    != null ? saved.month    : new Date().getMonth() + 1,
        year:     saved.year     != null ? saved.year     : new Date().getFullYear(),
        dateFrom: saved.dateFrom != null ? saved.dateFrom : "",
        dateTo:   saved.dateTo   != null ? saved.dateTo   : "",
      },
      // Snapshot of the filters that produced the data on screen.
      appliedFilters: null,
      dateConfig: { dateFormat: "Y-m-d", allowInput: true },
      roundOptions: [
        { ID: 0, Name: "ไม่เลือก" },
        { ID: 1, Name: "รอบที่ 1 (วันที่ 15)" },
        { ID: 2, Name: "รอบที่ 2 (สิ้นเดือน)" },
      ],
      monthOptions: [
        { ID: 0, Name: "ไม่เลือก" },
        { ID: 1, Name: "มกราคม" }, { ID: 2, Name: "กุมภาพันธ์" }, { ID: 3, Name: "มีนาคม" },
        { ID: 4, Name: "เมษายน" }, { ID: 5, Name: "พฤษภาคม" }, { ID: 6, Name: "มิถุนายน" },
        { ID: 7, Name: "กรกฎาคม" }, { ID: 8, Name: "สิงหาคม" }, { ID: 9, Name: "กันยายน" },
        { ID: 10, Name: "ตุลาคม" }, { ID: 11, Name: "พฤศจิกายน" }, { ID: 12, Name: "ธันวาคม" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      balance: "BranchMoney/getBalance",
      // clientPaginationMixin reads `tableData` for the policy-status list.
      tableData: "BranchMoney/getPolicyStatus",
      isLoadingBalance: "BranchMoney/isLoadingBalance",
      isLoadingPolicyStatus: "BranchMoney/isLoadingPolicyStatus",
    }),
    policyStatus() {
      return this.tableData || [];
    },
    byType() {
      return (this.balance && this.balance.ByType) || [];
    },
    byAccount() {
      return (this.balance && this.balance.ByAccount) || [];
    },
    poolTotal() {
      return (this.balance && this.balance.Total) || { sum_in: 0, sum_out: 0, balance: 0 };
    },
    movement() {
      return (this.balance && this.balance.Movement) || { has_range: false, sum_in: 0, sum_out: 0, balance: 0 };
    },
    movementNet() {
      return (this.movement.sum_in || 0) - (this.movement.sum_out || 0);
    },
    yearOptions() {
      const y = new Date().getFullYear();
      const out = [];
      for (let i = y - 2; i <= y + 1; i++) out.push(i);
      return out;
    },
    params() {
      // Backend (LMG_BO #31) reads snake_case query params. Range is
      // optional — only sent when both ends are set.
      const p = {
        round: this.searchParams.round,
        month: this.searchParams.month,
        year:  this.searchParams.year,
      };
      if (this.searchParams.dateFrom && this.searchParams.dateTo) {
        p.time_from = this.searchParams.dateFrom;
        p.time_to = this.searchParams.dateTo;
      }
      return p;
    },
    todayStamp() {
      const d = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
    },
    currentPeriodLabel() {
      const f = this.appliedFilters || this.searchParams;
      const monthName = (this.monthOptions.find((m) => m.ID === f.month) || {}).Name;
      const monthShown = f.month > 0 && monthName ? monthName : "ทั้งปี";
      const year = f.year || new Date().getFullYear();
      const roundName = (this.roundOptions.find((r) => r.ID === f.round) || {}).Name;
      const roundShown = f.round > 0 && roundName ? roundName : "ทุกรอบ";
      return `${roundShown} · ${monthShown} ${year}`;
    },
    rangeLabel() {
      const f = this.appliedFilters || this.searchParams;
      if (f.dateFrom && f.dateTo) return `${f.dateFrom} ถึง ${f.dateTo}`;
      if (f.dateFrom) return `ตั้งแต่ ${f.dateFrom}`;
      if (f.dateTo) return `ถึง ${f.dateTo}`;
      return "";
    },
  },
  watch: {
    searchParams: {
      deep: true,
      handler(val) {
        try { localStorage.setItem(LS_KEY, JSON.stringify(val)); } catch (_) {}
      },
    },
  },
  async mounted() {
    await this.search();
  },
  methods: {
    ...mapActions({
      loadBalance: "BranchMoney/loadBalance",
      loadPolicyStatus: "BranchMoney/loadPolicyStatus",
    }),
    async search() {
      this.appliedFilters = { ...this.searchParams };
      await Promise.all([
        this.loadBalance(this.params),
        this.loadPolicyStatus(this.params),
      ]);
    },
    // ── ByType helpers (find the row for an account_type, default to 0s) ──
    typeRow(t) {
      return this.byType.find((r) => r.account_type === t) || { sum_in: 0, sum_out: 0, balance: 0 };
    },
    typeBalance(t) {
      return this.typeRow(t).balance;
    },
    typeSumIn(t) {
      return this.typeRow(t).sum_in;
    },
    typeSumOut(t) {
      return this.typeRow(t).sum_out;
    },
    typeLabel(t) {
      if (t === 2) return "โอน";
      if (t === 3) return "สด";
      if (t === 1) return "สนญ.";
      if (t === 0) return "สำรองจ่าย";
      return "—";
    },
    typePillClass(t) {
      if (t === 2) return "calm__pill--blue";
      if (t === 3) return "calm__pill--success";
      if (t === 0) return "calm__pill--warning";
      return "calm__pill--neutral";
    },
    methodLabel(via) {
      // paid_via: 1=สด, 2=โอน, 3=เช็ค (mirrors payment.via convention)
      if (via === 1) return "สด";
      if (via === 2) return "โอน";
      if (via === 3) return "เช็ค";
      return "—";
    },
    formatBaht(n) {
      if (n == null) return "0";
      const v = Number(n);
      if (!isFinite(v)) return "0";
      return v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatNum(n) {
      if (n == null) return "0";
      return Number(n).toLocaleString("en-US");
    },
    formatMarker(n) {
      return String(n).padStart(3, "0");
    },
  },
};
</script>

<style scoped>
/* "Currently showing" badge — small inline pill. */
.finance-active-round {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  background: var(--c-blue-soft);
  color: var(--c-blue-deep);
  font-size: 13px;
  white-space: nowrap;
}
.finance-active-round i { font-size: 11px; }
.finance-active-round strong { font-weight: 600; }

.finance-table-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 18px 22px;
  border-bottom: 1px solid var(--c-border);
}
.finance-table-toolbar {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}
.finance-table-scroll {
  overflow-x: auto;
}
.finance-table-foot {
  padding: 14px 22px;
  margin-top: 0;
  border-top: 1px solid var(--c-border);
}

/* Movement row — reuses the finance cashrow look. */
.cashrow {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 0;
  background: var(--c-surface-soft);
  border: 1px solid var(--c-border);
  border-radius: 8px;
  overflow: hidden;
}
@media (max-width: 720px) {
  .cashrow { grid-template-columns: 1fr; }
}
.cashcell {
  padding: 16px 20px;
  border-right: 1px solid var(--c-border);
}
.cashcell:last-child { border-right: 0; }
.cashcell__label {
  font-size: 12px;
  color: var(--c-text-soft);
  margin-bottom: 6px;
  font-weight: 500;
}
.cashcell__value {
  font-size: 22px;
  font-weight: 600;
  color: var(--c-text);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
}
.cashcell--cleared .cashcell__value { color: var(--c-success); }
.cashcell--outstanding .cashcell__value { color: var(--c-warning); }

/* Per-account breakdown — responsive grid that wraps for dynamic N accounts. */
.acctgrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
}
.acctcell {
  padding: 16px 18px;
  background: var(--c-surface-soft);
  border: 1px solid var(--c-border);
  border-radius: 8px;
}
.acctcell__label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--c-text);
  font-weight: 600;
  margin-bottom: 8px;
}
.acctcell__value {
  font-size: 20px;
  font-weight: 600;
  color: var(--c-text);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
}
.acctcell__meta {
  margin-top: 6px;
  font-size: 12px;
  color: var(--c-text-soft);
  font-variant-numeric: tabular-nums;
}
</style>
